const MEASUREMENT_ID = "G-PTXRGNFEMY";
const PRODUCTION_HOSTS = new Set(["drivenow54.com", "www.drivenow54.com"]);
const VISIT_HOSTS = new Set(["drivenow54.com", "www.drivenow54.com", "localhost", "127.0.0.1"]);
const TOKEN_PREFIXES = ["/mobile-photos/", "/customer-info/", "/service-quote/"];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const VISITOR_KEY = "dn_visitor_id";

let lastPageKey = "";
let lastLookKey = "";

export function analyticsPath(pathname) {
  if (!pathname || typeof pathname !== "string") return null;
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") || "/" : pathname;
  if (path === "/admin" || path.startsWith("/admin/")) return null;
  for (const prefix of TOKEN_PREFIXES) {
    const bare = prefix.slice(0, -1);
    if (path === bare || path.startsWith(prefix)) return bare;
  }
  return path;
}

function debugEnabled() {
  try {
    return window.localStorage.getItem("dn-ga-debug") === "1";
  } catch {
    return false;
  }
}

function hostAllowed() {
  const host = window.location.hostname;
  if (PRODUCTION_HOSTS.has(host)) return true;
  return host === "localhost" && debugEnabled();
}

function visitHostAllowed() {
  return VISIT_HOSTS.has(window.location.hostname);
}

function siteLanguage() {
  const lang = (document.documentElement.lang || "").toLowerCase();
  return lang.startsWith("es") ? "es" : "en";
}

function publicItemId(value) {
  if (value == null || typeof value === "object") return "";
  const text = String(value).trim();
  if (!text || text.length > 120) return "";
  if (!/^[A-Za-z0-9][A-Za-z0-9 _.-]*$/.test(text)) return "";
  return text;
}

function publicItemName(value) {
  if (value == null || typeof value === "object") return "";
  const text = String(value).replace(/\s+/g, " ").trim();
  if (!text || text.length > 120 || text.includes("@")) return "";
  return text;
}

function visitorId() {
  try {
    const current = window.localStorage.getItem(VISITOR_KEY) || "";
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(current)) {
      return current.toLowerCase();
    }
    const created = window.crypto.randomUUID();
    window.localStorage.setItem(VISITOR_KEY, created);
    return created;
  } catch {
    return "";
  }
}

function recordVisit() {
  const id = visitorId();
  if (!id || !BACKEND_URL) return;
  fetch(`${BACKEND_URL}/api/analytics/visit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ visitor_id: id }),
    keepalive: true,
  }).catch(() => {});
}

function sendVehicleLook(itemId, itemName, context) {
  if (!itemId || typeof window.dnGtag !== "function" || !hostAllowed() || !ensureConfig()) return;
  const key = `${context}:${itemId}`;
  if (key === lastLookKey) return;
  lastLookKey = key;
  const params = { item_id: itemId, view_context: context };
  if (itemName) params.item_name = itemName;
  window.dnGtag("event", "view_item", params);
}

function ensureConfig() {
  if (typeof window.dnGtag !== "function" || !hostAllowed()) return false;
  if (window.__dnGa4Ready) return true;
  const debug = debugEnabled();
  window.dnGtag("js", new Date());
  window.dnGtag("config", MEASUREMENT_ID, { send_page_view: false, debug_mode: debug });
  window.__dnGa4Ready = true;
  return true;
}

export function trackPageView(pathname, extra) {
  const path = analyticsPath(pathname);
  if (!path) {
    lastPageKey = "";
    return;
  }
  if (path === lastPageKey) return;
  const measure = hostAllowed() && typeof window.dnGtag === "function";
  const remember = visitHostAllowed();
  if (!measure && !remember) return;
  lastPageKey = path;
  if (measure && ensureConfig()) {
    const params = {
      page_title: document.title,
      page_path: path,
      page_location: `${window.location.origin}${path}`,
      site_language: siteLanguage(),
    };
    const itemId = publicItemId(extra && extra.item_id);
    const itemName = publicItemName(extra && extra.item_name);
    if (extra && extra.content_group === "vehicle") {
      params.content_group = "vehicle";
      if (itemId) params.item_id = itemId;
      if (itemName) params.item_name = itemName;
    }
    window.dnGtag("event", "page_view", params);
    if (params.content_group === "vehicle") sendVehicleLook(itemId, itemName, "page");
  }
  if (remember) recordVisit();
}

export function trackVehicleLook(vehicle) {
  sendVehicleLook(
    publicItemId(vehicle && vehicle.id),
    publicItemName(vehicle && vehicle.name),
    "modal"
  );
}

export function trackVehicleTime(vehicle, elapsedMs) {
  const itemId = publicItemId(vehicle && vehicle.id);
  const ms = Math.round(Number(elapsedMs) || 0);
  if (!itemId || ms < 1000 || ms > 30 * 60 * 1000) return;
  if (typeof window.dnGtag !== "function" || !hostAllowed() || !ensureConfig()) return;
  if (lastLookKey === `modal:${itemId}`) lastLookKey = "";
  const params = {
    item_id: itemId,
    view_context: "modal",
    engagement_time_msec: ms,
    browse_seconds: Math.round(ms / 1000),
  };
  const itemName = publicItemName(vehicle && vehicle.name);
  if (itemName) params.item_name = itemName;
  window.dnGtag("event", "vehicle_browse", params);
}

export function trackLead(leadType, extra) {
  if (!leadType || typeof leadType !== "string") return;
  if (typeof window.dnGtag !== "function" || !hostAllowed()) return;
  if (!analyticsPath(window.location.pathname)) return;
  if (!ensureConfig()) return;
  const params = { lead_type: leadType, site_language: siteLanguage() };
  const itemId = publicItemId(extra && extra.item_id);
  if (itemId) params.item_id = itemId;
  window.dnGtag("event", "generate_lead", params);
}
